import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import {
  KibAddIcon,
  KibChevronDownIcon,
  KibChevronUpIcon,
} from '@chewy/kib-icons-react'
import { KibSpinner } from '@chewy/kib-interstitials-react'
import { KibTooltip } from '@chewy/kib-tooltip-react'
import {
  KibTruncation,
  KibTruncationContent,
  KibTruncationFocusTarget,
} from '@chewy/kib-truncation-react'
import classNames from 'classnames'

import type { AttachmentHistoryItem } from '~/types'

import { AttachmentMenuItem } from './AttachmentMenuItem'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import '@chewy/kib-truncation-styles/dist/kib-truncation.css'
import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import styles from './AttachmentMenu.module.scss'

const DEFAULT_VISIBLE_COUNT = 5
const SCROLL_THRESHOLD = 10
const POPPER_OFFSET: [number, number] = [140, 0]

const getAttachmentKey = (attachment: AttachmentHistoryItem, index: number) =>
  attachment.documentId ||
  attachment.id ||
  attachment.originalFileName ||
  attachment.fileName ||
  attachment.name ||
  `${index}`

const getAttachmentDocumentId = (attachment: AttachmentHistoryItem) =>
  attachment.documentId || attachment.id

interface AttachmentMenuProps {
  anchorRef: React.RefObject<HTMLElement>
  attachments: AttachmentHistoryItem[]
  isLoading?: boolean
  onAddAttachment: () => void
  onToggleAttachment: (documentId: string, nextValue: boolean) => void
  placement?: 'top' | 'right' | 'bottom' | 'left'
  selectedAttachmentIds: string[]
}

export const AttachmentMenu = ({
  anchorRef,
  attachments: expandedAttachments,
  selectedAttachmentIds,
  onAddAttachment,
  onToggleAttachment,
  isLoading = false,
  placement = 'top',
}: AttachmentMenuProps) => {
  const { t } = useTranslation(['Communications', 'Common'])
  const menuRef = useRef<HTMLDivElement | null>(null)
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const summaryAttachments = useMemo(
    () => expandedAttachments.slice(0, DEFAULT_VISIBLE_COUNT),
    [expandedAttachments],
  )
  const shouldTruncateList = expandedAttachments.length > DEFAULT_VISIBLE_COUNT
  const shouldScroll =
    isExpanded && expandedAttachments.length > SCROLL_THRESHOLD

  const handleAddAttachmentClick = () => {
    anchorRef.current?.click() // for hiding the menu
    onAddAttachment()
  }

  const handleToggleExpandedClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    setIsExpanded((prev) => !prev)
  }

  useEffect(() => {
    if (listScrollRef.current) {
      listScrollRef.current.scrollTop = 0
    }
  }, [isExpanded])

  return (
    <KibTooltip
      className={styles.menu}
      placement={placement}
      popperModifiers={[
        { name: 'offset', options: { offset: POPPER_OFFSET } },
        {
          name: 'flip',
          enabled: false,
        },
      ]}
      ref={menuRef}
      trigger={anchorRef}
    >
      <div role="menu">
        <div className={styles.header}>
          {t('Communications:ATTACHMENT_MENU.HEADER')}
        </div>
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <KibSpinner />
          </div>
        ) : (
          <>
            <KibTruncation open={isExpanded} reversible={false}>
              <KibTruncationContent
                summary={
                  <>
                    {summaryAttachments.map((attachment, index) => {
                      const documentId = getAttachmentDocumentId(attachment)
                      const isChecked = Boolean(
                        documentId &&
                          selectedAttachmentIds.includes(documentId),
                      )

                      return (
                        <AttachmentMenuItem
                          attachment={attachment}
                          checked={isChecked}
                          disabled={!documentId}
                          key={getAttachmentKey(attachment, index)}
                          onChange={(nextValue) => {
                            if (documentId) {
                              onToggleAttachment(documentId, nextValue)
                            }
                          }}
                        />
                      )
                    })}
                  </>
                }
              >
                <div
                  className={classNames({
                    [styles.listScrollable]: shouldScroll,
                  })}
                  ref={listScrollRef}
                >
                  {expandedAttachments.map((attachment, index) => {
                    const documentId = getAttachmentDocumentId(attachment)
                    const isChecked = Boolean(
                      documentId && selectedAttachmentIds.includes(documentId),
                    )

                    return (
                      <React.Fragment key={getAttachmentKey(attachment, index)}>
                        <AttachmentMenuItem
                          attachment={attachment}
                          checked={isChecked}
                          disabled={!documentId}
                          onChange={(nextValue) => {
                            if (documentId) {
                              onToggleAttachment(documentId, nextValue)
                            }
                          }}
                        />
                        {index === DEFAULT_VISIBLE_COUNT - 1 &&
                          shouldTruncateList && <KibTruncationFocusTarget />}
                      </React.Fragment>
                    )
                  })}
                </div>
              </KibTruncationContent>
            </KibTruncation>
            {shouldTruncateList && (
              <KibButtonNew
                className={styles.menuButton}
                emphasis="tertiary"
                icon={
                  isExpanded ? <KibChevronUpIcon /> : <KibChevronDownIcon />
                }
                theme="utility"
                onClick={handleToggleExpandedClick}
              >
                {isExpanded
                  ? t('Communications:ATTACHMENT_MENU.SHOW_LESS_ATTACHMENTS')
                  : t('Communications:ATTACHMENT_MENU.SHOW_ALL_ATTACHMENTS')}
              </KibButtonNew>
            )}
          </>
        )}
        <KibButtonNew
          className={styles.menuButton}
          emphasis="tertiary"
          icon={<KibAddIcon />}
          theme="utility"
          onClick={handleAddAttachmentClick}
        >
          {t('Communications:ATTACHMENT_MENU.ADD_ATTACHMENT')}
        </KibButtonNew>
      </div>
    </KibTooltip>
  )
}
